import { Page } from '@/lib/components/core/pageItems/page';
import { useGetHandmatigeInvoer, useSendHandmatigeInvoer, useValidate } from '@/lib/queries/manual_input';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Form, Space, Steps } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { StepProps } from 'antd/lib';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Step1 } from './step_1/page';
import { Step2 } from './step_2/page';
import { Step3 } from './step_3/page';
import { Step4 } from './step_4/page';
import { Step5 } from './step_5/page';
import { Afzender, Relatie } from '@/lib/types/relatie';
import { v4 as uuid } from 'uuid';
import { useMsal } from '@azure/msal-react';
import ResponseModal from './modals/responsemodal';
import { useNavigate } from 'react-router-dom';

export const ManualInputPage: React.FunctionComponent = () => {
  const { t } = useTranslation('input');
  const [form] = useForm<Relatie>();
  const navigate = useNavigate();

  const registratieJaar = Form.useWatch('registratiejaar', form);
  const uonummer = Form.useWatch('uoNummer', form);

  const [currentStep, setCurrentStep] = useState(0);
  const [responseContent, setResponseContent] = useState<string | null>(null);
  const [modalErrorContent, setModalErrorContent] = useState<string | null>(null);

  const [formAfzender, setFormAfzender] = useState<Afzender>({} as Afzender);

  const { instance: msalInstance } = useMsal();
  const { mutate: mutateHandmatigeInvoer, isPending: isSaving } = useSendHandmatigeInvoer();
  const { mutate: validate, isPending: isValidating } = useValidate();
  const {
    data: handmatigeInvoerData,
    isLoading: handmatigeInvoerIsLoading,
    isError: handmatigeInvoerIsError,
  } = useGetHandmatigeInvoer(registratieJaar, uonummer);

  useEffect(() => {
    if (handmatigeInvoerData) {
      form.setFieldsValue(handmatigeInvoerData);
    }
  }, [handmatigeInvoerData, form]);

  const [items, setItems] = useState<StepProps[]>([
    {
      status: 'process',
      title: 'Introductie',
      disabled: false,
    },
    {
      status: 'wait',
      title: 'Bedrijfsgegevens',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Milieugegevens',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Teeltplan',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Versturen',
      disabled: true,
    },
  ]);

  const goToStep = (step: number) => {
    if (step < 0 || step >= items.length || items[step]?.disabled) {
      return;
    }

    setCurrentStep(step);
  };

  const updateStep = (page: number) => {
    setItems(current => {
      const updated = [...current];
      updated[page].status = 'finish';
      updated[page].disabled = page === 0;

      if (updated[page + 1].status !== 'finish') {
        updated[page + 1].status = 'process';
        updated[page + 1].disabled = false;
      }
      setCurrentStep(page + 1);
      return updated;
    });
  };

  function createUpdatedAfzender(currentAfzender: Afzender, currentValues: Relatie, milieuValidatie: boolean, teeltplanRegelsValidatie: boolean): Afzender {
    return {
      ...currentAfzender,
      isMilieugegevensValidatie: milieuValidatie,
      isTeeltplanRegelsValidatie: teeltplanRegelsValidatie,
      relatie: [
        {
          ...currentAfzender.relatie[0],
          ...currentValues,
        },
      ],
    };
  }

  function updateAfzender(currentValues: Relatie, milieuValidatie: boolean, teeltplanRegelsValidatie: boolean) {
    setFormAfzender(prev => {
      const afzender = createUpdatedAfzender(prev, currentValues, milieuValidatie, teeltplanRegelsValidatie);
      if (currentStep === 2 || currentStep === 3) {
        validateAfzender(afzender);
      }
      return afzender;
    });
  }

  function validateAfzender(afzender) {
    validate(afzender, {
      onSuccess: (response: any) => {
        //Check if response is OK result
        const isSuccess = response?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 0));

        //Check if there are warnings
        const containsWarnings = response?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code != 0));

        if (isSuccess && currentStep !== 4) {
          if (containsWarnings) {
            //Only show warnings, not the OK result
            response.relaties[0].foutBerichten = response?.relaties[0].foutBerichten?.filter(foutBericht => foutBericht?.code !== 0);
            setResponseContent(JSON.stringify(response, null, 2));
          } else {
            updateStep(currentStep);
          }
        } else {
          setResponseContent(JSON.stringify(response, null, 2));
        }
      },
      onError: (response: any) => {
        setResponseContent(JSON.stringify(response, null, 2));
        setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
      },
    });
  }

  const onSubmitStep1 = async () => {
    form.validateFields().then(() => {
      const account = msalInstance.getActiveAccount();
      const username = account?.username || 'unknown';
      const currentValues = form.getFieldsValue();
      setFormAfzender(prev => {
        return {
          ...prev,
          berichtVersie: '03.00',
          berichtID: uuid(),
          timestamp: new Date().toISOString(),
          isDeelRapportage: currentValues['isDeelRapportage'] ? 1 : 2,
          isUpdate: true,
          loginNaam: username.toString(),
          relatie: [
            {
              registratiejaar: currentValues['registratiejaar'],
            },
          ] as Relatie[],
        };
      });
      updateStep(currentStep);
    });
  };

  const onSubmitStep2 = async () => {
    form.validateFields().then(() => {
      const currentRelatie = form.getFieldsValue();
      updateAfzender(currentRelatie, false, false);
      updateStep(currentStep);
    });
  };

  const onSubmitStep3 = async () => {
    try {
      form.validateFields().then(() => {
        const sanitizedRelatie = sanitize(form.getFieldsValue());
        updateAfzender(sanitizedRelatie, true, false);
      });
    } catch (e) {
      setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
    }
  };

  const onSubmitStep4 = async () => {
    try {
      form.validateFields().then(() => {
        updateAfzender(form.getFieldsValue(), false, true);
      });
    } catch (e) {
      setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
    }
  };

  const onSubmitStep5 = async () => {
    form.validateFields().then(() => {
      const currentRelatie = form.getFieldsValue();
      const afzender = createUpdatedAfzender(formAfzender, currentRelatie, false, false);
      setFormAfzender(afzender);
      submitAfzender(afzender);
    });
  };

  function sanitize(relatie: Relatie): Relatie {
    const updatedRelatie = {
      ...relatie,
      ...sanitizeLozingsVorm(relatie),
    };

    return {
      ...updatedRelatie,
      ...sanitizeMetingen(updatedRelatie),
      ...sanitizeLozingen(updatedRelatie),
    };
  }

  function sanitizeLozingen(relatie: Relatie) {
    if (!relatie.lozingen) {
      const nieuw = (relatie.lozingsVorm || []).map(lv => ({
        betrekkingTotTeeltwijze: lv.betrekkingTotTeeltwijze,
        lozingen: Array.from({ length: 13 }, (_, index) => ({
          periode: index + 1,
          hoeveelheid: 0,
        })),
      }));
      return { lozingen: nieuw };
    }

    let lozingen = relatie.lozingen.filter(l => l != null);
    (relatie.lozingsVorm || []).forEach(lozingsvorm => {
      if ([0, 4, 5].includes(lozingsvorm.value)) {
        lozingen = lozingen.filter(m => m.betrekkingTotTeeltwijze !== lozingsvorm.betrekkingTotTeeltwijze);
        lozingen.push({
          betrekkingTotTeeltwijze: lozingsvorm.betrekkingTotTeeltwijze,
          lozingen: Array.from({ length: 13 }, (_, index) => ({
            periode: index + 1,
            hoeveelheid: 0,
          })),
        });
      }
    });

    return { lozingen: lozingen };
  }

  function sanitizeLozingsVorm(relatie: Relatie) {
    let lozingsVormen = relatie.lozingsVorm?.filter(l => l !== undefined && l !== null);

    if (lozingsVormen === null || lozingsVormen === undefined) {
      return;
    }

    return {
      lozingsVorm: lozingsVormen,
    };
  }

  function sanitizeMetingen(relatie: Relatie) {
    let metingen = relatie.metingen?.filter(m => m !== undefined && m !== null);
    if (metingen === null || metingen === undefined) {
      return;
    }
    for (const meting of metingen) {
      for (let metingIndex = 0; metingIndex < meting.nH4Metingen.length; metingIndex++) {
        let nh4Meting = meting.nH4Metingen[metingIndex];
        if (meting.nO3Metingen[metingIndex] !== null) {
          meting.nO3Metingen[metingIndex].datum = nh4Meting.datum;
        }
        if (meting.pMetingen && meting.pMetingen[metingIndex] !== null) {
          meting.pMetingen[metingIndex].datum = nh4Meting.datum;
        }
        if (meting.naMetingen && meting.naMetingen[metingIndex] !== null) {
          meting.naMetingen[metingIndex].datum = nh4Meting.datum;
        }
      }
      meting.nH4Metingen = meting.nH4Metingen.filter(
        meting => (meting.datum !== null && meting.gehalte !== null && meting.gehalte !== undefined) || meting.geenMeting === true
      );
      meting.nO3Metingen = meting.nO3Metingen.filter(
        meting => (meting.datum !== null && meting.gehalte !== null && meting.gehalte !== undefined) || meting.geenMeting === true
      );
      meting.pMetingen = meting.pMetingen?.filter(
        meting => (meting.datum !== null && meting.gehalte !== null && meting.gehalte !== undefined) || meting.geenMeting === true
      );
      meting.naMetingen = meting.naMetingen?.filter(
        meting =>
          (meting.datum !== null && meting.gehalte !== null && meting.gehalte !== undefined) || meting.geenMeting === true || meting.geenNatrium === true
      );
    }
    return {
      metingen: metingen,
    };
  }

  const onModalOk = () => {
    setResponseContent(null);
    setModalErrorContent(null);

    let responseData = JSON.parse(responseContent || '');

    if (currentStep === 4) {
      if (responseData?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 0 || foutBericht?.code === 4))) {
        navigate('/');
        return;
      }
    } else if (responseData?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 4))) {
      updateStep(currentStep);
    }
  };

  const submitAfzender = (afzender: Afzender) => {
    try {
      mutateHandmatigeInvoer(afzender, {
        onSuccess: response => {
          setResponseContent(JSON.stringify(response, null, 2));
        },
        onError: response => {
          setResponseContent(JSON.stringify(response, null, 2));
          setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
        },
      });
    } catch (e) {
      setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
    }
  };

  const onCompleteFormSubmit = async () => {
    submitAfzender(formAfzender);
  };

  const steps = [
    <Step1 onSubmit={onSubmitStep1} isLoading={handmatigeInvoerIsLoading} isError={handmatigeInvoerIsError} />,
    <Step2 onSubmit={onSubmitStep2} />,
    <Step3 onSubmit={onSubmitStep3} isValidating={isValidating} />,
    <Step4 onSubmit={onSubmitStep4} isValidating={isValidating} />,
    <Step5 onSubmit={onSubmitStep5} isSaving={isSaving} />,
  ];

  const previousStep = currentStep - 1;
  const nextStep = currentStep + 1;
  const canGoPrevious = previousStep >= 0 && !items[previousStep]?.disabled;
  const canGoNext = nextStep < items.length && !items[nextStep]?.disabled;

  return (
    <Space style={{ width: '100%' }} direction="vertical">
      <div style={{ alignItems: 'center', display: 'flex', gap: '12px', margin: '0 auto', maxWidth: '1080px', width: '100%' }}>
        <Button
          aria-label="Ga naar de vorige stap"
          disabled={!canGoPrevious}
          icon={<LeftOutlined />}
          onClick={() => goToStep(previousStep)}
          shape="circle"
          type="primary"
        />
        <Steps
          style={{ flex: 1, marginBottom: '20px' }}
          type="navigation"
          size="small"
          current={currentStep}
          onChange={goToStep}
          className="site-navigation-steps"
          items={items}
        />
        <Button
          aria-label="Ga naar de volgende stap"
          disabled={!canGoNext}
          icon={<RightOutlined />}
          onClick={() => goToStep(nextStep)}
          shape="circle"
          type="primary"
        />
      </div>
      <Page title={t(`form.title.${currentStep}`)}>
        <Form onFinish={onCompleteFormSubmit} form={form}>
          {steps[currentStep]}
        </Form>
      </Page>
      <ResponseModal responseContent={responseContent} error={modalErrorContent} onOk={onModalOk} />
    </Space>
  );
};
